import { useTranslation } from 'react-i18next';
import type { CSSProperties } from 'react';
import { formatPrice } from '@/components/ui/Price';
import {
  deriveTableState,
  elapsedLabel,
  hasGuestPaymentPending,
  hasPaidRelance,
  hasPendingRound,
  isLocked,
  pendingItemsCount,
  waitingLabel,
} from './tableState';
import { formatTicketTime } from './ticketTime';
import UnsentCartBadge from './UnsentCartBadge';
import type { WaiterTable } from './waiterApi';

interface Props {
  table: WaiterTable;
  onOpenFree: (table: WaiterTable) => void;
  onNavigateOccupied: (table: WaiterTable) => void;
  onTransfer: (table: WaiterTable) => void;
  /** DF-0223 — ou mene la pastille rouge. Absent, elle ouvre la table comme la
   *  tuile : jamais un bouton qui ne fait rien. */
  onOpenPending?: (table: WaiterTable) => void;
  /** SC-976 — rattachées visibles à côté de cette ancre : la tablée entière est
   *  alors rendue en UNE carte large (`columnSpan` colonnes), les rattachées ne
   *  sont plus des tuiles à part. */
  members?: WaiterTable[];
  columnSpan?: number;
  /** DF-0383 — plats saisis sur cet appareil pour cette table, pas envoyés. */
  unsentQty?: number;
}

/** SC-976 — rattachées listées une à une sur la tuile ancre ; au-delà, un compte. */
const MAX_LISTED_EXTRAS = 2;

function priceToCents(value: string): number {
  return Math.round(parseFloat(value) * 100);
}

/**
 * Spec 2026-08-28-waiter-salle-ux-design.md §2.2 D3 — one table tile in the
 * Sur place grid, rendered for EVERY table (free included, unlike the old
 * `NotesListPage` note-card which only showed OPEN sessions). Colour comes
 * from `deriveTableState` (`.w-tile-<state>`, `waiter-root.css`); the red
 * pending badge markup is copied verbatim from the old note-card so it stays
 * a SIBLING of the button (never nested interactive content).
 */
export default function TableTile({
  table,
  onOpenFree,
  onNavigateOccupied,
  onTransfer,
  onOpenPending,
  members = [],
  columnSpan = 2,
  unsentQty = 0,
}: Props) {
  const { t } = useTranslation();
  const state = deriveTableState(table);
  const pending = hasPendingRound(table);
  const locked = isLocked(table);
  const payingOnline = hasGuestPaymentPending(table);
  const paidRelance = hasPaidRelance(table);
  const guest = table.session?.openedBy === 'guest';
  const session = table.session;
  const pendingCount = pendingItemsCount(session?.pendingValidation ?? []);
  const elapsed = session ? elapsedLabel(session.openedAt) : '';
  // DF-0383 — une table à prendre affiche son attente, pas son heure
  // d'ouverture : c'est elle qu'on lit, et « 13:52 · 59 min » ne tient pas sur
  // une tuile de 111px (mesuré à 360px). L'heure reste dans le nom accessible.
  const waited = session && state === 'seated' ? waitingLabel(session.openedAt) : '';
  const elapsedTitle =
    session && waited
      ? t('waiter.notes.salle.waitingSince', { time: formatTicketTime(session.openedAt), duration: waited })
      : elapsed
        ? t('waiter.notes.salle.openedSince', { duration: elapsed })
        : undefined;

  // SC-976 design §5.1 — a member tile is a pared-down twin of its anchor's:
  // no covers, no time, no total (a « ⛓ 5 » tag instead). Its fill is the
  // group's colour washed out, with a dashed ring, so it never reads as a
  // second table with its own order,
  // no pending badge (that lives on the anchor only, see `NotesListPage`'s
  // filter). `isMerged` alone gates every bit of this markup, so a plain
  // single-table session renders byte-identical to before (E-42).
  const merged = session?.isMerged === true;
  const isAnchorTile = merged && table.role === 'anchor';
  const isMemberTile = merged && table.role === 'member';
  const unsentBadgeId = session && unsentQty > 0 && !isMemberTile ? `table-tile-unsent-${table.uuid}` : undefined;
  const tableNumbers = session?.tableNumbers ?? [];
  // D3 — ancre en tête de `tableNumbers`, contrat garanti par le back (jamais
  // recalculé par égalité de valeur : review P2#4, un filtre par inégalité
  // suppose que `table.number` s'écrit identiquement à `tableNumbers[0]`,
  // ce que le contrat ne promet pas).
  const extraNumbers = isAnchorTile ? tableNumbers.slice(1) : [];
  const anchorNumber = isMemberTile ? (tableNumbers[0] ?? '') : '';
  // Au-delà de deux rattachées, la liste « +14+15+16… » ne tient plus sur une
  // tuile de 96px et s'entasse sous le ⋮ : on n'affiche plus que leur nombre
  // (« +5 tables »). La liste complète reste dans le title de la ligne.
  const extrasCollapsed = extraNumbers.length > MAX_LISTED_EXTRAS;
  const fullLabel = isAnchorTile ? [table.number, ...extraNumbers].join('+') : undefined;
  const isGroupCard = isAnchorTile && members.length > 0;
  const groupNumbers = isGroupCard ? [table.number, ...extraNumbers] : [];

  // DF-0349 — une table partie en caisse redevenait une case vide sans un mot :
  // chez Yuji le 2026-10-01, deux tables envoyées par erreur ont été rouvertes et
  // ressaisies en entier, ce qui a mis deux tickets en double dans la caisse. La
  // case libre garde l'heure de l'envoi et qui l'a fait (sale_api ne renvoie la
  // trace que sur une table libre, et 30 min au plus).
  const caisseTrace = session === null ? (table.lastCaisseHandoff ?? null) : null;
  const caisseTraceTime = caisseTrace ? formatTicketTime(caisseTrace.at) : '';

  const handleClick = () => {
    if (session === null) {
      onOpenFree(table);
    } else {
      onNavigateOccupied(table);
    }
  };

  // SC-976 — carte de tablée. `columnSpan` 1 = tablée courte (2 tables) posée
  // dans une tuile normale, puces centrées ; ≥ 2 = carte large, puces en haut,
  // couverts/heure en bas au début et montant en bas à la fin, pour occuper la
  // largeur au lieu d'en laisser la moitié vide.
  const wideCard = isGroupCard && columnSpan > 1;
  const groupChips =
    isGroupCard &&
    (wideCard ? (
      // SC-1011 (retour QA) — carte large (3 tables et plus) : « 5+4+3 » écrit
      // comme la tuile carrée et comme un numéro seul, 28px gras, sans les
      // pastilles blanches en 17px. `pe-9` (sens de la page) garde le ⋮ libre ;
      // la taille ne descend que si l'étiquette ne tient pas sur une ligne,
      // jamais sous 20px, et au-delà elle passe à la ligne entre deux numéros
      // (jamais au milieu d'un « 12 »). Le `bdi` ltr garde « 5+4+3 » en arabe.
      <span
        title={fullLabel}
        className="max-w-full pe-9 font-sans font-bold text-[28px] leading-none"
        style={{
          fontSize: `max(20px, min(28px, calc((100cqw - 36px) / ${(groupNumbers.join('+').length * 0.62).toFixed(2)})))`,
        }}
      >
        <bdi dir="ltr" className="flex flex-wrap items-baseline">
          {groupNumbers.map((n, i) => (
            <span
              key={`${n}-${i}`}
              data-testid={`table-tile-chip-${session?.tableUuids?.[i] ?? n}`}
              className="max-w-full truncate"
            >
              {i > 0 && '+'}
              {n}
            </span>
          ))}
        </bdi>
      </span>
    ) : (
      // Tuile carrée : « 1+2 » écrit comme le numéro d'une table seule (SC-1011 :
      // l'ancienne pastille blanche en 15px ne ressemblait à aucun numéro).
      // Centrée, même « 1+2 » en 28px passe sous le ⋮ d'une tuile de 96px
      // (mesuré : le « 10 » d'une table seule tient tout juste) : `me-[25px]`
      // (sens de la page, comme le ⋮) la recentre entre le bord de début et le
      // ⋮. 28px tant qu'elle y tient ; au-delà (« S12+S13 »), la taille suit la
      // largeur de la tuile au lieu de tronquer. Sans `cqw`, le style en ligne
      // est rejeté et la classe 28px s'applique.
      <span
        title={fullLabel}
        data-testid={`table-tile-chip-${table.uuid}`}
        className="me-[25px] font-sans font-bold text-[28px] leading-none max-w-[calc(100%-25px)] truncate"
        style={{
          fontSize: `min(28px, calc((100cqw - 25px) / ${(groupNumbers.join('+').length * 0.62).toFixed(2)}))`,
        }}
      >
        <bdi dir="ltr">{groupNumbers.join('+')}</bdi>
      </span>
    ));
  const coversRow = (
    <>
          {session && !isMemberTile && (
            <span className="flex items-center gap-1 font-sans text-[16px] leading-tight max-w-full">
              {locked && (
                <span aria-hidden="true" className="leading-none">
                  🔒
                </span>
              )}
              {payingOnline && (
                /* spec 2026-08-28 §6 — le client paie sur son telephone. Il rejoint
                   la ligne des couverts comme 🔒 et QR : le nombre de lignes de la
                   tuile ne doit pas dependre de son etat (D3). */
                <span
                  data-testid="table-tile-guest-paying"
                  role="img"
                  aria-label={t('waiter.notes.card.guestPaying')}
                  title={t('waiter.notes.card.guestPaying')}
                  className="leading-none"
                >
                  💳
                </span>
              )}
              {guest && (
                <span data-testid="table-tile-guest-mark" aria-hidden="true" className="font-bold leading-none">
                  QR
                </span>
              )}
              <span className="truncate">{t('waiter.notes.card.covers', { n: session.covers })}</span>
            </span>
          )}
    </>
  );
  const elapsedRow = (
    <>
          {session && !isMemberTile && (
            /* live-review finding m3 — beyond 60 min this used to spell out
               "Ouverte depuis 25 h 24" in full, wrapping onto 3 lines and
               overlapping the ⋮ button. The compact `HH:MM · 1 h 20`
               (spec §2.7) is the only visible text now; the full sentence
               survives as the accessible name / tooltip. */
            <span
              data-testid={`table-tile-elapsed-${table.uuid}`}
              aria-label={elapsedTitle}
              title={elapsedTitle}
              className={`font-sans text-[16px] leading-tight max-w-full whitespace-nowrap truncate${waited ? ' font-semibold' : ''}`}
            >
              {/* ltr isolé : en arabe « 59 min » s'affichait « min 59 », et
                  « 14:31 · 1 h 04 » se lisait à l'envers. */}
              <bdi dir="ltr">
                {waited || (
                  <>
                    {formatTicketTime(session.openedAt)}
                    {elapsed && ` · ${elapsed}`}
                  </>
                )}
              </bdi>
            </span>
          )}
    </>
  );
  const totalRow = (
    <>
          {session && isMemberTile ? (
            // SC-976 design §5.1 — l'étiquette « ⛓ 5 » prend la place du total
            // sur une tuile rattachée : le montant est celui de la tablée, il
            // ne se répète pas table par table. Peinte en plein aux couleurs de
            // la tablée, elle dit à quelle tuile se rattacher.
            <span
              data-testid={`table-tile-merged-with-${table.uuid}`}
              aria-label={t('waiter.notes.card.mergedWith', { number: anchorNumber })}
              title={t('waiter.notes.card.mergedWith', { number: anchorNumber })}
              className={`w-tile-${state} border-0 rounded-full px-2 py-0.5 font-sans text-[13px] font-bold leading-tight max-w-full truncate`}
            >
              <span aria-hidden="true">⛓ {anchorNumber}</span>
            </span>
          ) : (
            session && (
              /* Le montant n'est plus réservé à la tablette (`hidden sm:inline`) :
                 sur téléphone c'est le chiffre que le serveur cherche le plus. Pas
                 de <Price>, qui impose `text-ink` et casserait la couleur d'état
                 (`.w-tile-*`, contraste calculé ≥ 4,5:1 sur chaque fond). */
              <span
                data-testid={`table-tile-total-${table.uuid}`}
                className={`font-mono font-bold leading-none max-w-full truncate ${wideCard ? 'text-[18px] shrink-0' : 'text-[16px]'}`}
              >
                {formatPrice(priceToCents(session.runningTotal))}
              </span>
            )
          )}
    </>
  );

  return (
    <div className="relative" style={isGroupCard ? { gridColumn: `span ${columnSpan}` } : undefined}>
      <button
        type="button"
        data-testid={`table-tile-${table.uuid}`}
        data-state={state}
        data-pending={pending}
        data-locked={locked}
        data-guest={guest}
        data-merged={isAnchorTile ? 'anchor' : isMemberTile ? 'member' : undefined}
        data-group-size={isGroupCard ? groupNumbers.length : undefined}
        aria-describedby={unsentBadgeId}
        onClick={handleClick}
        // D3 "anneau 3px" — no Tailwind `border`/`border-[3px]` utility here:
        // `.w-tile-${state}` (waiter-root.css) already owns border-width AND
        // colour, and waiter-root.css loads before tailwind.css (main.tsx),
        // so a same-specificity Tailwind class would silently win on source
        // order and collapse the ring to 1px (review F1F2#4).
        className={
          wideCard
            ? `relative w-full h-full min-h-[96px] rounded-xl shadow-soft py-2 px-2.5 flex flex-col justify-between gap-1 text-start w-tile-${state}`
            : `relative w-full aspect-square min-h-[96px] rounded-xl shadow-soft p-1.5 flex flex-col items-center justify-center gap-0.5 ${isMemberTile ? 'w-tile-member gap-1.5' : `w-tile-${state}`}`
        }
        style={
          wideCard
            ? // Même hauteur qu'une tuile carrée voisine (gouttière de 6px comprise).
              // `containerType` : référence du `100cqw` de l'étiquette (SC-1011).
              { aspectRatio: `${columnSpan * 110 + (columnSpan - 1) * 6} / 110`, containerType: 'inline-size' }
            : isGroupCard
              ? // SC-1011 — référence du `100cqw` de l'étiquette « 1+2 ».
                { containerType: 'inline-size' }
            : isMemberTile
              ? ({
                  '--w-link': `var(--w-state-${state}-ring)`,
                  '--w-link-bg': `var(--w-state-${state}-bg)`,
                  '--w-link-fg': `var(--w-state-${state}-fg)`,
                } as CSSProperties)
              : undefined
        }
      >
        {wideCard ? (
          <>
            {groupChips}
            <span className="flex items-end justify-between gap-2 w-full">
              <span className="flex flex-col items-start gap-0.5 min-w-0">
                {coversRow}
                {elapsedRow}
              </span>
              {totalRow}
            </span>
          </>
        ) : (
          <>
            {isGroupCard ? (
              groupChips
            ) : (
              <span
                title={fullLabel}
                className="font-sans font-bold text-[28px] leading-none max-w-full truncate"
              >
                {table.number}
              </span>
            )}
            {/* SC-976 — ancre seule à l'écran (ses rattachées sont filtrées, autre
                salle) : une pastille blanche à cheval sur son bord haut, « ⛓ +7 »,
                dit qu'elle n'est pas une table ordinaire. */}
            {isAnchorTile && !isGroupCard && (
              <span
                data-testid={`table-tile-merged-extras-${table.uuid}`}
                className="pointer-events-none absolute -top-1.5 left-1/2 -translate-x-1/2 max-w-[calc(100%+0.5rem)] truncate whitespace-nowrap rounded-full border-2 bg-white px-1.5 font-sans text-[11px] font-bold leading-[16px]"
                style={{ borderColor: `var(--w-state-${state}-ring)`, color: `var(--w-state-${state}-fg)` }}
              >
                <span aria-hidden="true">⛓</span>
                {extraNumbers.length > 0 && ' '}
                {/* Numéros de table en ltr isolé : en arabe, « +7 » s'affichait « 7+ ». */}
                {extraNumbers.length > 0 &&
                  (extrasCollapsed ? (
                    t('waiter.notes.card.mergedCount', { n: extraNumbers.length })
                  ) : (
                    <bdi dir="ltr">{`+${extraNumbers.join('+')}`}</bdi>
                  ))}
              </span>
            )}
            {/* One line per fact, always in the same order — the tile now carries
                four of them (couverts, ouverture, durée, montant) and the old
                layout stacked 🔒 / QR as extra rows, so the same table jumped from
                3 to 5 rows depending on its state. 🔒 and QR ride the couverts
                row: whatever the state, the stack stays at three lines under the
                number. 16px / 16px / 28px: measured 2026-09-14 with Fira Sans on the
                6px-gap grid, a 360px screen gives 111px tiles and every row fits
                except a 10 h+ duration, which truncates (full text in the title). */}
            {coversRow}
            {elapsedRow}
            {totalRow}
            {caisseTrace && (
              <span
                data-testid={`table-tile-caisse-trace-${table.uuid}`}
                title={
                  caisseTrace.by
                    ? t('waiter.notes.card.caisseTraceAtBy', { time: caisseTraceTime, name: caisseTrace.by })
                    : t('waiter.notes.card.caisseTraceAt', { time: caisseTraceTime })
                }
                className="flex flex-col items-center max-w-full font-sans text-[14px] leading-tight"
              >
                {/* Plus de mot « En caisse » sur la case (retiré le 2026-10-03 à la
                    demande du client) : l'heure et le nom restent, la phrase entière
                    vit dans le title et dans la modale « Ouvrir la table ». */}
                {/* L'heure d'abord, et seul le nom se tronque, à sa propre fin :
                    tronquée d'un bloc, la ligne coupait en arabe le DÉBUT d'un nom
                    latin (« …upont · 16:33 », revue design du 2026-10-01). */}
                <span className="flex max-w-full items-baseline whitespace-nowrap">
                  <span className="shrink-0">{caisseTraceTime}</span>
                  {caisseTrace.by && (
                    <>
                      <span className="shrink-0">{' · '}</span>
                      <bdi dir="auto" className="min-w-0 truncate">
                        {caisseTrace.by}
                      </bdi>
                    </>
                  )}
                </span>
              </span>
            )}
          </>
        )}
      </button>
      {/* DF-0223 — la pastille est la seule chose que le serveur regarde quand
          une tournee de client attend, et elle ne faisait rien : il ouvrait la
          table, tombait sur « Imprimer » (= addition) et sur ✔ (= fin de
          service), et la commande restait en file. Elle mene desormais a la
          feuille de la ronde. Elle reste un FRERE du bouton de tuile, jamais un
          enfant : deux boutons imbriques sont un HTML invalide. */}
      {pending && !isMemberTile && (
        <button
          type="button"
          data-testid="note-card-pending"
          aria-label={t('waiter.notes.card.pendingAction', { n: pendingCount })}
          onClick={() => (onOpenPending ?? onNavigateOccupied)(table)}
          className="absolute -top-1.5 -start-1.5 min-w-[22px] h-[22px] px-1 rounded-full text-white font-sans text-[13px] font-bold flex items-center justify-center"
          style={{ backgroundColor: 'var(--w-danger)' }}
        >
          {pendingCount}
        </button>
      )}
      {paidRelance && session && !isMemberTile && (
        /* Spec 2026-08-28 paid-table-ordering-lock §5.2 — cette table a deja
           paye, puis rappele : son convive se croit quitte et peut se lever.
           Badge orthogonal (comme « a valider »), et non un 5e etat : les
           quatre couleurs de tuile viennent d'etre livrees, on n'y touche pas.

           Il porte le RESTE, le chiffre a reclamer ; la tuile continue
           d'afficher le cumul, comme toutes les autres. Ce montant etait peint
           en vert (« de l'argent deja rentre ») : sur une surface ou le vert
           signifie « table soldee », un chiffre du en vert se lit « deja
           paye » — exactement le contresens que ce badge existe pour eviter.
           Ambre (`--w-owed`), et surmonte du mot « Reste » pour qu'aucun
           coup d'oeil ne puisse le prendre pour un encaissement. Le libelle
           s'empile DANS le badge, qui flotte : le nombre de lignes de la tuile
           elle-meme ne bouge pas (D3). */
        <span
          data-testid={`table-tile-paid-relance-${table.uuid}`}
          role="img"
          aria-label={t('waiter.notes.card.paidRelance', {
            paid: formatPrice(priceToCents(session.paidTotal ?? '0')),
            due: formatPrice(priceToCents(session.dueTotal ?? '0')),
          })}
          title={t('waiter.notes.card.paidRelance', {
            paid: formatPrice(priceToCents(session.paidTotal ?? '0')),
            due: formatPrice(priceToCents(session.dueTotal ?? '0')),
          })}
          className="absolute -bottom-1.5 -start-1.5 px-1.5 py-px rounded-lg text-white flex flex-col items-center leading-none"
          style={{ backgroundColor: 'var(--w-owed)' }}
        >
          <span className="font-sans text-[8px] font-bold uppercase tracking-wide leading-none">
            {t('waiter.notes.card.remainingShort')}
          </span>
          <span className="font-mono text-[11px] font-bold leading-none">
            {formatPrice(priceToCents(session.dueTotal ?? '0'))}
          </span>
        </span>
      )}
      {unsentBadgeId && <UnsentCartBadge qty={unsentQty} id={unsentBadgeId} />}
      {session && (
        <button
          type="button"
          data-testid={`transfer-table-${session.uuid}`}
          aria-haspopup="dialog"
          aria-label={t('waiter.overview.actions')}
          onClick={(event) => {
            event.stopPropagation();
            onTransfer(table);
          }}
          className="absolute top-0 end-0 w-11 h-11 flex items-center justify-center font-sans text-lg leading-none"
        >
          ⋮
        </button>
      )}
    </div>
  );
}
